<script lang="ts">
	import Actirise, { Mode, PageType } from '$lib/components/Actirise.svelte';
	import AdPlugg from '$lib/components/AdPlugg.svelte';
	import BigSquare from '$lib/components/ArticleGrid/BigSquare.svelte';
	import Image from '$lib/components/Image.svelte';
	import LdTag from '$lib/components/LdTag.svelte';
	import Primis from '$lib/components/Primis.svelte';
	import ShareBar from '$lib/components/ShareBar.svelte';
	import { translations } from '$lib/i18n/translations';
	import { AspectRatio } from '$lib/satellite.types';
	import { getGravatarUrl } from '$lib/utils/ArticleHelper';
	import { DateFormatter } from '$lib/utils/DateFormatter';
	import { isActirise } from '$lib/utils/PropertyMode';
	import { fullArticleSchema } from '$lib/utils/SchemaHelper';
	import { afterUpdate } from 'svelte';
	import type { PageData } from './$types';

	export let data: PageData;

	const property = data.property;
	const colors = property.colors;
	const t = translations(property.language);

	let contentContainer: HTMLDivElement;

	$: article = data.article;
	$: content = data.hydratedContent;
	$: populars = property.populars ?? [];
	$: preview = data.preview;
	$: canonical = data.seo.canonical;
	$: explicit = data.explicit;

	afterUpdate(() => {
		window.__canonical = canonical;

		/* AdPlugg */
		const element = document.getElementsByClassName('adplugg-wrapper')?.[0];
		if (element) {
			new AdPlugg({
				target: element,
				props: {
					code: property.adPlugg?.code ?? '',
					zone: property.adPlugg?.zone ?? ''
				}
			});
		}

		/* Hyperembed */
		const hyperembed = contentContainer.querySelectorAll<HTMLElement>('figure[data-hyperembed]');
		if (hyperembed?.length > 0) {
			if (typeof window.iframely === 'object') {
				window.iframely.load();
				return;
			}
			var ifs = document.createElement('script');
			ifs.type = 'text/javascript';
			ifs.async = true;
			ifs.src = 'https://embed.attrnum.com/embed.js';
			document.getElementsByTagName('head')[0].appendChild(ifs);
		}
	});
</script>

<svelte:head>
	<meta name="author" content={article.anonymous ? property.name : article.author.name} />

	<meta property="og:type" content="article" />

	{#if article.anonymous}
		<meta property="article:author" content={`https://${property.hostname}`} />
	{:else}
		<meta
			property="article:author"
			content={`https://${property.hostname}/informations/${property.language == 'fr' ? 'nos-redacteurs' : 'our-writers'}/${article.author.slug}`}
		/>
	{/if}

	<meta property="article:section" content={article.category.name} />
	<meta property="article:tag" content={article.keywords.join(', ')} />
	<meta property="article:published_time" content={article?.published?.toISOString()} />
	{#if article.modified}
		<meta property="article:modified_time" content={article?.modified?.toISOString()} />
	{/if}

	<meta property="og:image" content={article.imageSocial.url} />
	<meta property="og:image:type" content="image/jpeg" />
	<meta property="og:image:alt" content={article.title} />
	<meta property="og:image:width" content={article.imageSocial.size[0].toString()} />
	<meta property="og:image:height" content={article.imageSocial.size[1].toString()} />

	{#if !isActirise(property) && property?.adsenseId && !explicit}
		<script
			async
			src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client={property.adsenseId}"
			crossorigin="anonymous"
		></script>
	{/if}
</svelte:head>

{#if preview}
	<div class="w-full bg-red-500 text-white text-center p-2 mb-5 text-3xl">⚠ PREVIEW MODE ⚠</div>
{/if}

{#if isActirise(property)}
	<Actirise
		mode={Mode.PAGE}
		type={PageType.ARTICLE}
		parameters={{
			custom1: data.cmp.mode,
			custom2: article.acd,
			custom3: (article.keywords[0] ?? '').toLowerCase()
		}}
		disableAds={!!explicit}
	/>
{/if}

<LdTag schema={fullArticleSchema(article, property)}></LdTag>

<div class="max-w-screen-lg mx-auto">
	<div class="lg:px-4 xl:px-0">
		<figure class="aspect-[5/2]">
			{#key article.imageFeatured}
				<Image
					blurhash={article.imageFeaturedBlurHash}
					image={article.imageFeatured}
					aspectRatio={AspectRatio._5BY2}
					alt={article.title}
					classList="w-full h-full lg:rounded-lg object-cover shadow-sm lg:shadow-lg"
					sizes="(max-width: 1023px) 100vw, (min-width: 1024px) 1024px, 100vw"
					loading="eager"
					fetchpriority="high"
				/>
			{/key}
			<figcaption class="text-right pr-4 lg:pr-0 text-xs mt-1 text-gray-500">
				{#if article.credit?.url}
					<a href={article.credit.url} target="_blank" rel="noopener noreferrer"
						>{article.credit?.text}</a
					>
				{:else}
					{#if article.credit?.text}
						{article.credit?.text}
					{/if}
					&nbsp;
				{/if}
			</figcaption>
		</figure>
	</div>

	<!-- Title line -->
	<div class="max-w-screen-lg px-4 xl:px-0 mx-auto">
		<div class="mb-1 text-sm">
			<span class="inline-block">
				<a
					href={`/${article.category.slug}`}
					class="uppercase group font-medium text-sm tracking-wider transition-colors duration-300 hover:{colors.hover} {colors.text}"
					>{article.category.name}
					<span
						class="block max-w-0 group-hover:max-w-full transition-all duration-300 {colors.background} group-hover:{colors.hoverBackground} h-0.5"
					></span>
				</a></span
			>
		</div>

		<h1 class="text-3xl font-serif">{article.title}</h1>
		<p class="text-lg">{article.description}</p>

		<div class="flex mt-3 gap-3 items-center">
			<div>
				{#if article.anonymous}
					<img
						loading="lazy"
						src="/icon.svg"
						alt={property.name}
						class="w-8 h-8 aspect-square rounded-full"
					/>
				{:else}
					<a
						href={`/informations/${property.language == 'fr' ? 'nos-redacteurs' : 'our-writers'}/${article.author.slug}`}
					>
						<img
							loading="lazy"
							src={getGravatarUrl(article.author.gravatarId, 64)}
							alt={article.author.name}
							class="w-8 h-8 aspect-square rounded-full"
						/>
					</a>
				{/if}
			</div>
			<div class="text-xs">
				<p class="font-semibold">
					{#if article.anonymous}
						{property.name}
					{:else}
						<a
							class="hover:underline underline-offset-2"
							rel="author"
							href={`/informations/${property.language == 'fr' ? 'nos-redacteurs' : 'our-writers'}/${article.author.slug}`}
							>{article.author.name}</a
						>
					{/if}
				</p>
				<ul class="list-none flex flex-col lg:flex-row">
					<li>
						<time datetime={article.published.toISOString()} itemprop="datePublished">
							{t('byline_published')}
							{DateFormatter.format(article.published, property.language)}
						</time>
					</li>
					{#if article.modified && !DateFormatter.sameDate(article.published, article.modified)}
						<li class="lg:before:content-['·'] lg:before:mx-2">
							<time datetime={article.modified.toISOString()} itemprop="dateModified">
								{t('byline_updated')}
								{DateFormatter.format(article.modified, property.language)}
							</time>
						</li>
					{/if}
				</ul>
			</div>
		</div>
	</div>

	<div class="max-w-screen-lg px-4 xl:px-0 mx-auto mt-4 [content-visibility:auto]">
		<ShareBar {article} {canonical} {property} />

		{#if property?.primisId != ''}
			{#key article._id}
				<Primis id={property.primisId} />
			{/key}
		{/if}

		<div class="flex flex-col lg:flex-row lg:gap-6 mt-4">
			<div class="lg:flex-grow max-w-lg lg:max-w-none print:max-w-none mx-auto">
				<div
					bind:this={contentContainer}
					class="prose prose-lg prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-ol:list-decimal prose-ul:list-disc prose-li:m-0"
				>
					<!-- eslint-disable-next-line svelte/no-at-html-tags -->
					{@html content}
				</div>

				{#if article.source?.text}
					<div class="bg-gray-100 p-4 mt-4 text-xs rounded-lg mb-4 lg:mb-0">
						<strong>Source:</strong>
						{#if article.source?.url}
							<a href={article.source.url} target="_blank" rel="noopener noreferrer"
								>{article.source?.text}</a
							>
						{:else}
							{article.source?.text}
						{/if}
					</div>
				{/if}
			</div>
			<div class="basis-[300px] lg:flex-grow-0 lg:flex-shrink-0 print:hidden">
				<div class="max-w-lg lg:max-w-none mx-auto">
					{#if isActirise(property) && !explicit}
						<div data-actirise-slot="aside-desktop" class="device-tablet device-desktop ads"></div>
					{/if}
					<div class="space-y-6">
						{#each populars as popular}
							<BigSquare description={false} article={popular} />
						{/each}
					</div>
				</div>
				<div class="lg:sticky lg:top-6 max-w-lg lg:max-w-none mx-auto mt-6">
					{#if isActirise(property) && !explicit}
						<div data-actirise-slot="aside-desktop-btf" class="device-desktop ads"></div>
					{/if}
				</div>
			</div>
		</div>
	</div>
</div>
